import { ClientResponse } from '@orbis-systems/orbis-api-clients/orbis';
import { flatMap, isArray } from 'lodash-es';
import { inject, singleton } from 'tsyringe';
import { CachedResponseApi } from '../BaseApi';
import { ICache } from '../Cache';
import {
    AccountStatus,
    BondQuantityType,
    CorporateActionsType,
    OrderFilterType,
    OverExposedType,
    PositionType,
    PriceType,
    UserType,
} from '../constants/lookups';
import { ApiInjectToken, MiddlewareInjectToken } from '../constants/tokens';
import {
    Acat,
    Apis,
    BranchInfo,
    CaseSubmitData,
    GroupRtbs,
    MiddlewareConfig,
    Order,
    OrderWithExtraInfo,
} from '../types';
import {
    Account,
    AccountWithExtraInfo,
    OverExposedAccount,
    PositionAccount,
    PositionAccountWithExtraInfo,
    Transfer,
} from '../types/Account.type';
import { CorporateAction, CorporateActionWithExtraInfo } from '../types/CorporateAction.type';
import { Model } from '../types/Model.type';
import { Portfolio, PortfolioWithExtraInfo } from '../types/Portfolio.type';
import { User } from '../types/User.type';
import {
    formatCorporateActions,
    formatOrders,
    formatOverExposedAccounts,
    formatPortfolio,
    formatPositionAccounts,
} from '../utils/dataUtils';
import { divide, sumBy } from '../utils/numberUtils';
import type { GroupApi } from './GroupApi';
import type { ModelApi } from './ModelApi';
import type { UserApi } from './UserApi';

@singleton()
export class BranchApi extends CachedResponseApi {
    constructor(
        @inject(MiddlewareInjectToken.Apis) apis: Apis,
        @inject(MiddlewareInjectToken.Config) config: MiddlewareConfig,
        @inject(MiddlewareInjectToken.Cache) cache: ICache,
        @inject(ApiInjectToken.ModelApi) private readonly modelApi: ModelApi,
        @inject(ApiInjectToken.GroupApi) private readonly groupApi: GroupApi,
        @inject(ApiInjectToken.UserApi) protected readonly userApi: UserApi,
    ) {
        super(apis, config, cache);
    }

    async getInfo(): Promise<BranchInfo> {
        const data = await Promise.all([this.getAccounts(), this.modelApi.getModels(), this.groupApi.getGroups()]);

        const accounts = data[0];
        const models = data[1];
        const groups = data[2];

        return {
            balance: sumBy(accounts, (account) => account.balance),
            cash: sumBy(accounts, (account) => account.cashOnAccount),
            monthlyCashflow: divide(
                sumBy(accounts, (account) => account.totalDividendAmount),
                12,
            ),
            portfolioValue: sumBy(accounts, (account) => account.markToMarket),
            totalAccounts: accounts.length,
            totalModels: models.length,
            totalGroups: groups.length,
        };
    }

    async getUsers(userTypeFilter?: UserType | UserType[]): Promise<User[]> {
        const { data } = await this.apis.orbis.advisory.userAccountManagement.list();
        if (userTypeFilter) {
            return (data as User[]).filter((user) =>
                isArray(userTypeFilter) ? userTypeFilter.includes(user.userType) : user.userType === userTypeFilter,
            );
        } else {
            return data;
        }
    }

    async getAccounts(): Promise<AccountWithExtraInfo[]> {
        const [userInfo, branchRtbsResponse] = await Promise.all([
            this.userApi.getUserInfo(),
            this.apis.orbis.branch.branchRtbs(),
        ]);

        const accounts = userInfo.accountList;
        const rtbsByAccountId = new Map((branchRtbsResponse.data as GroupRtbs[]).map((item) => [item.accountId, item]));

        return accounts
            .filter((account) => account.status === AccountStatus.Active)
            .map((account) => {
                const {
                    balance = 0,
                    cashOnAccount = 0,
                    markToMarket = 0,
                    sodCashBalance = 0,
                    sodMarginBalance = 0,
                    sodShortBalance = 0,
                    totalDividendAmount = 0,
                    totalDividendYield = 0,
                } = rtbsByAccountId.get(account.accountId) || {};

                return {
                    ...account,
                    balance,
                    cashOnAccount,
                    markToMarket,
                    sodCashBalance,
                    sodMarginBalance,
                    sodShortBalance,
                    totalDividendAmount,
                    totalDividendYield,
                };
            });
    }

    async getAccount(accountId: number): Promise<AccountWithExtraInfo | null> {
        const accounts = await this.getAccounts();

        const account = accounts.find((account) => account.accountId === accountId);

        if (!account) {
            return null;
        }

        return account;
    }

    async getInventoryAccounts(): Promise<Account[]> {
        return (await this.apis.orbis.branch.branchInventory()).data;
    }

    async getPositionAccounts(symbol: string): Promise<PositionAccountWithExtraInfo[]> {
        const data = await Promise.all([
            this.apis.orbis.user.positionSearch({ symbols: symbol, loadQuotes: true }),
            this.getPortfolio({ combined: false }),
        ]);

        const positionAccounts = data[0].data as PositionAccount[];
        const branchPortfolio = data[1];

        return formatPositionAccounts(symbol, positionAccounts, branchPortfolio);
    }

    async getPortfolio(options?: {
        combined?: boolean;
        currency?: string;
        symbol?: string;
        positionType?: PositionType;
    }): Promise<PortfolioWithExtraInfo> {
        const { combined = true, currency, symbol, positionType } = options || {};
        const data = await Promise.all([
            this.getAccounts(),
            this.getInventoryAccounts(),
            this.apis.orbis.branch.branchRtb(),
            this.apis.orbis.branch.branchPortfolio({
                // @ts-expect-error: // FIXME: missing query param (currency). Waiting for backend update
                currency,
                combined,
                loadQuotes: true,
                loadIndustries: true,
                loadRtb: true,
                marketCapBreakdown: true,
            }),
        ]);

        const accounts = data[0];
        const inventoryAccounts = data[1];
        const balance = data[2].data.balance || 0;
        const portfolio = data[3].data as Portfolio;

        return formatPortfolio(portfolio, accounts, inventoryAccounts, balance, { symbol, positionType: positionType });
    }

    async getOrders(
        filterType: OrderFilterType | OrderFilterType[] = OrderFilterType.Latest,
        symbol?: string,
    ): Promise<OrderWithExtraInfo[]> {
        const data = await Promise.all([
            this.apis.orbis.advisory.models.listing(),
            this.userApi.getUserInfo(),
            this.getInventoryAccounts(),
            ...(isArray(filterType) ? filterType : [filterType]).map((filter) => {
                return this.apis.orbis.orders.listingForBranch(`${filter}`, {
                    // @ts-expect-error // FIXME: some fields (loadQuotes, symbol) don't exist, waiting for backend to update
                    loadQuotes: true,
                    symbol,
                    bondPriceType: PriceType.Decimal,
                    bondQuantityType: BondQuantityType.Unit,
                });
            }),
        ]);

        const models = data[0].data as Model[];
        const accounts = data[1].accountList;
        const inventoryAccounts = data[2] as Account[];
        const orders = flatMap(data.slice(3) as ClientResponse[], (item) => item.data) as Order[];

        return formatOrders({
            orders,
            models,
            accounts,
            inventoryAccount: inventoryAccounts[0],
        });
    }

    async getOverExposedAccounts(threshold: number, type: OverExposedType): Promise<OverExposedAccount[]> {
        const { data } = await this.apis.orbis.advisory.analytics.overexposureAnalysis(`${type}`, 'branch', 'all', {
            threshold,
        });

        const accounts = await this.getAccounts();

        return formatOverExposedAccounts(data, type, accounts);
    }

    async getCorporateActions(
        symbols: string[],
        dateFrom: string,
        dateTo: string,
        types?: CorporateActionsType,
    ): Promise<CorporateActionWithExtraInfo[]> {
        const positions = (await this.getPortfolio()).entries;
        const actions = (
            await this.apis.orbis.research.corporateActions.search({
                symbol: symbols.join(','),
                dateFrom,
                dateTo,
                type: types,
            })
        ).data as CorporateAction[];

        return formatCorporateActions(actions, positions);
    }

    async getNotifications() {
        const response = await this.apis.middleware.message.getMessages();
        return response.data;
    }

    async markNotificationsAsSeen() {
        await this.apis.middleware.message.markAllAsSeen();
    }

    async getBranchMoneyTransfers(start?: number, perPage?: number): Promise<Transfer[]> {
        const response = await this.apis.orbis.transfers.transferStatusBranch({ start, perPage });
        return response.data as Transfer[];
    }

    async getBranchAcatTransfer(): Promise<Acat[]> {
        const response = await this.apis.orbis.transfers.acats.search({}, { loadAccounts: true });
        return response.data;
    }

    async createCase(data: CaseSubmitData) {
        // FIXME: missing from docs. Waiting for backend update
        const response = await this.apis.orbis.getApiClient().post('v1/branch/salesforce/ticket', {}, { params: data });
        return response.data;
    }
}
